import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowBack, Edit } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { collectionsHeaderHeight } from '../../../utils/consts/collectionsConsts';
import IconButton from '../../shared/IconButton';
import Localized from '../../shared/Localized';
import { List, Skeleton, Typography } from '@mui/material';
import { PresetListItem } from '../PresetListItem';
import { getMediaGridTemplateColumns } from '../../../utils/styleUtils';
import { useData } from '../../../data-layer';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import HistoryManager from '../../shared/HistoryManager';
import RevisionManager from '../../shared/RevisionManager';
import { Stack } from '@mui/system';
import { UserBadge } from '../../shared/UserBadge';
import ShadowScroller from '../../shared/ShadowScroller';
import { DisplayAsOptions } from '../../../utils/types/genericTypes';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../Routes';
import { VersionedDocumentStatus } from '../../../API';
import DraftBadge from '../../shared/DraftBadge';
import NoMediaItems from '../../shared/NoMediaItems';
import { useConfirm, useLocales } from '../../../hooks';
import { AllHeroPresetTypes } from '../../../utils/uiModuleUtils';
import Button from '../../shared/Button';
import { PushPin, PushPinOutlined } from '@mui/icons-material';
import { PinAllDialog } from '../PinAllDialog';
import { Sortable, SortableGroups } from '../../shared/Sortable';
import { PresetsSkeleton } from '../PresetsSkeleton';
import { EditorNotes } from '../../shared/EditorNotes';
import { usePermissions } from '../../../hooks/Permissions/usePermissions';
import { withHistoryManager } from '../../../state/General/withHistoryManager';
import { usePermissionsGuard } from '../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../state/theme';
import { HPCManagerTestIds } from '../../shared/TestsIds';
import { PermissionsGroupBadge } from '../../shared/PermissionsGroupBadge';

export const HISTORY_MANAGER_ID = 'hpc';

const useStyles = makeStyles<{ displayAs: DisplayAsOptions }>()((theme, { displayAs }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: collectionsHeaderHeight,
    background: theme.palette.background.paper,
    padding: theme.spacing(3, 4, 3, 1),
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  history: {
    width: 'min-content'
  },
  pinAll: {
    marginLeft: 'auto'
  },
  list: {
    minHeight: '100%',
    height: '100%',
    overflowX: 'hidden'
  },
  grid: {
    display: 'grid',
    minHeight: '100%',
    gridTemplateColumns: getMediaGridTemplateColumns(displayAs),
    gridTemplateRows: 'repeat(auto-fit, minmax(0, max-content))',
    gridGap: theme.spacing(2),
    padding: theme.spacing(2)
  },
  empty: {
    position: 'absolute',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(12),
    inset: 0,
    pointerEvents: 'none'
  }
}));

type HPCManagerProps = {
  collectionId: string;
  onBack?: () => unknown;
};

export default function HPCManager({ collectionId, onBack }: HPCManagerProps): React.ReactElement {
  const {
    canSave,
    canDelete,
    canPublish: canPublishGuard
  } = usePermissionsGuard({
    homepageOption: HomepageOptions.HPC
  });
  const {
    heroPresetCollections: {
      state: { withDisplayAs, withRecordById, withSelected, withSelectedPresets },
      hook: { edit, getRevisions, queueIdToFetch, update, publish, setPresetsPinned }
    },
    presets: {
      state: { withDataCache: withPresetsCache },
      hook: { queueIdToFetch: queuePresetIdToFetch }
    }
  } = useData();

  const presetsCache = useRecoilValue(withPresetsCache);
  const [isPinAllOpen, setIsPinAllOpen] = useState(false);

  // The selected collection from recoil
  const [selectedCollection, setSelectedCollection] = useRecoilState(withSelected);
  const setSelectedPresets = useSetRecoilState(withSelectedPresets);
  // Determine if the collection passed is the selected one
  const isSelected = useMemo(() => selectedCollection?.entityId === collectionId, [collectionId, selectedCollection]);

  // The collection provided in props
  const collection = useRecoilValue(isSelected ? withSelected : withRecordById(collectionId));
  const [presets, setPresets] = useState<string[]>([]);
  const isLoading = useMemo(() => !collection, [collection]);

  const { t } = useLocales();
  const navigate = useNavigate();
  const displayAs = useRecoilValue(withDisplayAs);
  const { classes, cx } = useStyles({ displayAs });
  const { hasPermissions } = usePermissions();
  const { UPSERT: canUpsert, PUBLISH: canPublish } = hasPermissions(collection?.ownerPermissionsGroups);

  const { isClean: isHistoryManagerClean } = useRecoilValue(withHistoryManager);
  const { confirmUnsavedChanges } = useConfirm();

  const isList = displayAs === DisplayAsOptions.LIST;

  useEffect(() => {
    if (!collection && collectionId) {
      queueIdToFetch(collectionId);
    }
  }, [collectionId]);

  useEffect(() => {
    if (!collection) return;
    setPresets(collection.presets);
  }, [collection]);

  const handleOnBack = () => {
    navigate(AppRoutes.heroPresetCollections());
    onBack?.();
  };

  const handleOnEdit = async () => {
    const shallContinue = isHistoryManagerClean || (await confirmUnsavedChanges());
    if (shallContinue) {
      edit(collectionId);
    }
  };

  const handleOnSave = async () => {
    if (collection) {
      await update(collection.entityId, collection);
    }
  };

  const handleOnPublish = async () => {
    if (collection) {
      await publish(collection.entityId);
    }
  };

  // Every preset in the collection is already rendered by a PresetListItem, which populates the cache
  const areAllPinned = useMemo(
    () => !!presets.length && presets.every((id) => (presetsCache[id]?.object?.data as AllHeroPresetTypes)?.isPinned),
    [presets, presetsCache]
  );

  // The endpoint pins the collection's *saved* presets, so unsaved edits would be applied to a stale list
  const handleOpenPinAll = async () => {
    const shallContinue = isHistoryManagerClean || (await confirmUnsavedChanges());

    if (shallContinue) {
      setIsPinAllOpen(true);
    }
  };

  const handleConfirmPinAll = async () => {
    const { failed } = await setPresetsPinned(collectionId, !areAllPinned);

    // Refresh so the toggled state is reflected without a manual reload
    if (failed.length < presets.length) {
      presets.forEach((presetId) => queuePresetIdToFetch(presetId));
    }

    return failed;
  };

  const getRevisionsPromise = useCallback(
    async (limit?: number, page?: number) => {
      if (collection && isSelected) {
        return getRevisions(collection.entityId, limit, page);
      }
    },
    [collection, getRevisions]
  );

  const onSetList = (newPresets: string[]) => {
    setPresets(newPresets);
    if (isSelected) {
      setSelectedPresets(newPresets);
    }
  };

  return (
    <div className={classes.container} data-testid={HPCManagerTestIds.root}>
      <div className={classes.topbar}>
        <IconButton onClick={handleOnBack} data-testid={HPCManagerTestIds.backButton}>
          <ArrowBack />
        </IconButton>
        {collection ? (
          <Stack spacing={3} direction="row" alignItems="center" overflow="hidden">
            <Typography className={classes.title} variant="h6" data-testid={HPCManagerTestIds.title}>
              <Localized prop={collection.name} />
            </Typography>
            {collection.status === VersionedDocumentStatus.DRAFT && (
              <DraftBadge data-testid={HPCManagerTestIds.draft} />
            )}
          </Stack>
        ) : (
          <Skeleton width={300} />
        )}
        <IconButton onClick={handleOnEdit} disabled={!canUpsert || !canSave} data-testid={HPCManagerTestIds.editButton}>
          <Edit />
        </IconButton>
        {isSelected && (
          <RevisionManager
            getRevisionsPromise={getRevisionsPromise}
            state={collection}
            onChange={setSelectedCollection}
          />
        )}
      </div>
      <div className={classes.details}>
        <Stack direction="row" gap={2} alignItems="center">
          <UserBadge record={collection} useEntityCreatedDate />
          {collection?.ownerPermissionsGroups && <PermissionsGroupBadge values={collection?.ownerPermissionsGroups} />}
          <EditorNotes object={collection} />
          {!!presets.length && canUpsert && canSave && (
            <Stack className={classes.pinAll} direction="row" gap={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('layouts.dynamic_hero_config')}
              </Typography>
              <Button
                size="small"
                color="grey"
                startIcon={areAllPinned ? <PushPinOutlined /> : <PushPin />}
                onClick={handleOpenPinAll}
                data-testid={HPCManagerTestIds.pinAllButton}
              >
                {t(areAllPinned ? 'hpc.unpin_all' : 'hpc.pin_all')}
              </Button>
            </Stack>
          )}
        </Stack>
        {isSelected && (
          <div className={classes.history}>
            <HistoryManager
              id={HISTORY_MANAGER_ID}
              state={collection}
              onChange={setSelectedCollection}
              saveCallback={handleOnSave}
              publishCallback={handleOnPublish}
              hasUpsertPermission={canSave && canUpsert}
              hasPublishPermission={canPublishGuard && canPublish && !!collection?.presets.length}
            />
          </div>
        )}
      </div>
      <ShadowScroller loading={isLoading}>
        {isLoading ? (
          <PresetsSkeleton />
        ) : (
          <List className={classes.list} disablePadding data-testid={HPCManagerTestIds.presetListLeft}>
            {!presets.length && (
              <div className={classes.empty}>
                <NoMediaItems
                  header={t('general.no_media_items.header')}
                  message={t('general.no_media_items.message')}
                />
              </div>
            )}
            <Sortable
              className={cx({ [classes.grid]: !isList, [classes.list]: isList })}
              group={{
                name: SortableGroups.presets,
                pull: canUpsert && canSave ? 'clone' : undefined,
                put: canUpsert && canSave
              }}
              list={presets}
              disabled={!canUpsert || !canSave}
              setList={onSetList}
              animation={200}
              sort={canUpsert && canSave}
              ghostClass="sortableGhost"
              filter=".sortableFiltered"
              isStringList
            >
              {presets.map((preset) => (
                <PresetListItem
                  key={preset}
                  presetId={preset}
                  isEditing
                  canDelete={canDelete && canUpsert}
                  isGridPreset
                />
              ))}
            </Sortable>
          </List>
        )}
      </ShadowScroller>
      {isPinAllOpen && (
        <PinAllDialog
          isPinned={!areAllPinned}
          presetCount={presets.length}
          onConfirm={handleConfirmPinAll}
          onClose={() => setIsPinAllOpen(false)}
        />
      )}
    </div>
  );
}
